/**
 * The two date shapes the player screens share: an absolute day for the
 * identity card's Joined and the cloud save's Created, and a compact
 * relative time — "40 seconds ago", "2 hours ago", "1 day ago", an absolute
 * date past a week — for the identity card's Last updated and the cloud
 * save's Updated. One module, so the two cards read one clock.
 */

// UTC, like the exact instant below it: a card that printed one date in
// the browser's zone and the other in UTC would show a save created at
// midnight UTC on the previous day to an operator five hours west.
const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

/** "Feb 14, 2026". */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return DATE_FMT.format(d);
}

/** "40 seconds ago", "2 hours ago", "1 day ago", or "Aug 30, 2026" past a week. */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  // Under a second — in practice a server clock a shade ahead of the
  // browser's, which would otherwise print «-1 seconds ago».
  if (sec < 1) return "just now";
  if (sec < 60) return ago(sec, "second");
  const min = Math.floor(sec / 60);
  if (min < 60) return ago(min, "minute");
  const hr = Math.floor(min / 60);
  if (hr < 24) return ago(hr, "hour");
  const day = Math.floor(hr / 24);
  if (day < 7) return ago(day, "day");
  return DATE_FMT.format(d);
}

function ago(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? "" : "s"} ago`;
}

const UTC_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: "UTC",
});

/** "Sep 3, 2026, 12:41 UTC" — the exact instant under a relative time. */
export function formatUTC(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${UTC_FMT.format(d)} UTC`;
}

/**
 * "18 Aug, 14:02" — the shape the read-only cards' tables were drawn in: day
 * before month on a 24-hour clock. That is `en-GB`, not the `en-US` the rest of
 * the panel formats dates with: en-US would put the month first and hang an
 * AM/PM on the time, and neither is what those columns were drawn as.
 * (LeaderboardConfigForm and the rewards export already reach for en-GB for the
 * same reason.) No year: these lists are the last 20 things a player did, read
 * next to "30 days" figures, so the year is noise in every row it would fit.
 */
const WHEN_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

/** "18 Aug, 14:02". */
export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return WHEN_FMT.format(d);
}

const DAY_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
});

/**
 * "4 Sep" — the same clock with the time dropped, for the lines where WHEN a
 * thing happened is the answer and the minute of it is not: a reward granted,
 * a trigger reached.
 */
export function formatDay(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return DAY_FMT.format(d);
}
